import * as React from 'react';
import { classNames } from '@/utils';

export interface ButtonProps {
  /** The child elements to be rendered inside the button (required). */
  children: React.ReactNode;
  /**
   * The variant of the button to render (optional):
   * - 'primary': a standard primary button with background color
   * - 'secondary': a secondary button with outline styling
   * - 'inline': an inline button without background or outline styling
   */
  variant?: 'primary' | 'secondary' | 'inline' | '';
  /**
   * The color of the button (optional):
   * - 'success': a green color typically used for positive actions
   * - 'danger': a red color typically used for negative actions
   */
  color?: 'success' | 'danger';
  /** Whether the button should be disabled (optional). */
  disabled?: boolean;
  /** Whether the button should submit a form (optional). */
  submit?: boolean;
  /** A function to be called when the button is clicked (optional). */
  onClick?(): void;
  /** CSS class names to be applied to the button element (optional). */
  className?: string;
  /** Inline styles to be applied to the button element (optional). */
  style?: React.CSSProperties;
  /** Whether the button should render as an icon button (optional). */
  isIconButton?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { children, submit, variant = 'primary', color, disabled, className, style, onClick, isIconButton } = props;

  return (
    <button
      ref={ref}
      style={style}
      onClick={onClick}
      disabled={disabled}
      type={submit ? 'submit' : 'button'}
      className={classNames('button', variant, color, { disabled, icon: isIconButton }, className)}
    >
      {children}
    </button>
  );
});

Button.displayName = 'Button';
